import React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import styles from './EditMeetingAttendanceDialog.module.scss';
import { getAbsoluteTimestamp } from '../../../../util/formatDate';
import FormControl from '@mui/material/FormControl';
import LoadingButton from '@mui/lab/LoadingButton';
import FormAlert from '../FormAlert/FormAlert';
import { useParams } from 'react-router-dom';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import { DummyUserPortrait } from '../../User/Team/Meetings/Meeting/UserPortrait/UserPortrait';
import APIClient from '../../../../util/APIClient';

const ATTENDANCE_OPTIONS = [
  {
    key: 'PRESENT',
    value: 'PRESENT',
    label: 'Present',
    description:
      'Team member is present. This status denotes their attendance in the meeting without having to sign into the application using their own account.',
  },
  {
    key: 'ABSENT',
    value: 'ABSENT',
    label: 'Away on leave',
    description:
      'Team member is away on annual leave or otherwise engaged, but their absence during the meeting is justified. This status will not negatively affect their score.',
  },
  {
    key: 'AWOL',
    value: 'AWOL',
    label: 'Absent (without official leave)',
    description:
      'Team member is absent without the official leave. This status will have a negative impact on their score by effectively marking it as missed meeting.',
  },
  {
    key: 'JOINED',
    value: 'JOINED',
    label: 'Present (joined the meeting)',
    description:
      'Team member has joined the meeting. This status is equivalent to being "Present", with the exception of being applied automatically when the team member joins the meeting.',
  },
  {
    key: 'UNKNOWN',
    value: 'UNKNOWN',
    label: 'Unknown',
    description:
      'The attendance status is currently unknown. This is likely to be the case if the meeting status has been edited manually, but the attendance has not been updated.',
  },
];

function EditMeetingAttendanceDialog({ isOpen, close, meeting, reload, path }) {
  const [formAlert, setFormAlert] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const attendanceData = meeting.attendances.map((a) => {
    const teamMember = meeting.team.teamMembers.filter(
      (t) => t.cognitoId === a.cognitoId,
    )?.[0];
    const { firstName = '?', lastName = '?' } = teamMember || {};
    return { ...a, firstName, lastName };
  });
  const [attendances, setAttendances] = React.useState(attendanceData);

  React.useEffect(() => {
    setAttendances(
      meeting.attendances.map((a) => {
        const teamMember = meeting.team.teamMembers.filter(
          (t) => t.cognitoId === a.cognitoId,
        )?.[0];
        const { firstName = '?', lastName = '?' } = teamMember || {};
        return { ...a, firstName, lastName };
      }),
    );
  }, [meeting]);

  function handleAttendanceChange(newAttendance) {
    setAttendances((current) => {
      const newAttendances = [...current];
      const index = newAttendances.findIndex(
        (a) => a.cognitoId === newAttendance.cognitoId,
      );
      if (index !== -1) {
        newAttendances[index] = newAttendance;
      }
      return newAttendances;
    });
  }

  const handleClose = () => {
    setAttendances(attendanceData);
    setFormAlert(null);
    setIsSubmitting(false);
    close();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await APIClient.put(
        path,
        attendances.map((a) => ({
          cognitoId: a.cognitoId,
          attendance: a.attendance,
        })),
      );
      reload();
      handleClose();
    } catch {
      setFormAlert({
        severity: 'error',
        title: 'Could not update attendance!',
        message:
          'Something went wrong when updating meeting attendance. Please contact the administrator for further support.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAbort = () => {
    handleClose();
  };

  const handleFocusLost = () => {
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleFocusLost}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">Edit Attendance</DialogTitle>
      <div className={styles.content}>
        <DialogContentText id="alert-dialog-description">
          You are currently editing the attendance for the meeting of Team
          &quot;
          {meeting.team.label}&quot;, originally scheduled for{' '}
          {getAbsoluteTimestamp(new Date(meeting.scheduledTime), true, true)}.
        </DialogContentText>
        <div className={styles.form}>
          {attendances.map((a) => (
            <div
              key={`${a.cognitoId}-attendance-form`}
              className={styles.attendanceEntry}
            >
              <div className={styles.name}>
                <DummyUserPortrait
                  cognitoId={a.cognitoId}
                  firstName={a.firstName}
                  lastName={a.lastName}
                />
                <p>
                  {a.firstName} {a.lastName}
                </p>
              </div>
              <FormControl className={styles.formControl} fullWidth>
                <Select
                  variant="standard"
                  labelId={`${a.cognitoId}-attendance-label`}
                  id={`${a.cognitoId}-attendance`}
                  label="Attendance"
                  onChange={(e) =>
                    handleAttendanceChange({
                      ...a,
                      attendance: e.target.value,
                    })
                  }
                  value={a.attendance}
                  renderValue={(selected) =>
                    ATTENDANCE_OPTIONS.filter((o) => o.value === selected)?.[0]
                      ?.label
                  }
                >
                  {ATTENDANCE_OPTIONS.map((o) => (
                    <Tooltip
                      key={o.key}
                      title={
                        <p>
                          <strong>{o.label}</strong> - <em>{o.description}</em>
                        </p>
                      }
                      placement="right"
                      value={o.value}
                    >
                      <MenuItem
                        disabled={['JOINED', 'UNKNOWN'].includes(o.value)}
                        key={o.key}
                      >
                        {o.label}
                      </MenuItem>
                    </Tooltip>
                  ))}
                </Select>
              </FormControl>
            </div>
          ))}
        </div>
        <div className={styles.buttons}>
          <Button onClick={handleAbort} color="primary" variant="outlined">
            Cancel
          </Button>
          <LoadingButton
            loading={isSubmitting}
            color="primary"
            variant="contained"
            type="submit"
            onClick={handleSubmit}
          >
            Update
          </LoadingButton>
        </div>
        {formAlert && <FormAlert alert={formAlert} />}
      </div>
    </Dialog>
  );
}

/**
 * Must be placed within a path that contains the teamId and meetingId parameters.
 */
export function TeamEditMeetingAttendanceDialog(props) {
  const { meetingId, teamId } = useParams();
  return (
    <EditMeetingAttendanceDialog
      {...props}
      path={`/user/teams/${teamId}/meetings/${meetingId}/attendance`}
    />
  );
}

/**
 * Must be placed within a path that contains the teamId and meetingId parameters.
 */
export function AdministratorEditMeetingAttendanceDialog(props) {
  const { meetingId, teamId } = useParams();
  return (
    <EditMeetingAttendanceDialog
      {...props}
      path={`/administrator/teams/${teamId}/meetings/${meetingId}/attendance`}
    />
  );
}
